
import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Resource } from "../hooks/useResourceManagement";

interface AddResourceAssignmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  formData: any;
  setFormData: (data: any) => void;
  resources: Resource[];
  onSave: () => void;
  onResourceChange: (resourceId: string) => void;
  existingAssignments?: any[];
  selectedTaskId?: string | null;
}

const AddResourceAssignmentDialog = ({
  isOpen,
  onClose,
  formData,
  setFormData,
  resources,
  onSave,
  onResourceChange,
  existingAssignments = [],
  selectedTaskId
}: AddResourceAssignmentDialogProps) => {
  // Get available resources for the selected task
  const getAvailableResourcesForTask = () => {
    if (!selectedTaskId) {
      return [];
    }

    // Get assigned resources for this specific task
    const assignedResources = existingAssignments.filter(assignment => 
      assignment.task_id === selectedTaskId
    );

    console.log('Assigned resources for task:', assignedResources);
    console.log('All resources:', resources);

    // Create a set of unique identifiers for assigned resources based on name, role_type, and role
    const assignedResourceKeys = new Set(
      assignedResources.map(assignment => {
        const key = `${assignment.name || ''}_${assignment.role_type || ''}_${assignment.role || ''}`;
        console.log('Assigned resource key:', key);
        return key;
      })
    );

    console.log('Assigned resource keys:', Array.from(assignedResourceKeys));

    // Filter resources that are NOT already assigned to this task
    const availableResources = resources.filter(resource => {
      const resourceKey = `${resource.name || ''}_${resource.role_type || ''}_${resource.role || ''}`;
      console.log('Checking resource key:', resourceKey, 'Is assigned?', assignedResourceKeys.has(resourceKey));
      return !assignedResourceKeys.has(resourceKey);
    });

    console.log('Available resources after filtering:', availableResources);

    // Remove duplicates from available resources based on the same criteria
    const uniqueAvailableResources = [];
    const seenKeys = new Set();

    for (const resource of availableResources) {
      const resourceKey = `${resource.name || ''}_${resource.role_type || ''}_${resource.role || ''}`;
      if (!seenKeys.has(resourceKey)) {
        seenKeys.add(resourceKey);
        uniqueAvailableResources.push(resource);
      }
    }

    console.log('Final unique available resources:', uniqueAvailableResources);
    
    return uniqueAvailableResources.sort((a, b) => a.name.localeCompare(b.name));
  };

  const availableResources = getAvailableResourcesForTask();

  // Get selected resource details
  const selectedResource = resources.find(r => r.id === formData.resource_id);

  const formatRateWithCommas = (value: string) => {
    const num = parseFloat(value.replace(/,/g, ''));
    if (isNaN(num)) return value;
    return num >= 1000 ? num.toLocaleString('en-US') : value;
  };

  const handleAllocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, allocated_percentage: e.target.value });
  };

  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/,/g, '').replace(/-/g, '');
    setFormData({ ...formData, rate: rawValue });
  };

  const handleAllocationBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') return;
    
    const numValue = Number(value);
    if (numValue < 1) {
      setFormData({ ...formData, allocated_percentage: 1 });
    } else if (numValue > 100) {
      setFormData({ ...formData, allocated_percentage: 100 });
    }
  };

  const handleRateBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') return;
    
    const raw = value.replace(/,/g, '');
    const numValue = Number(raw);
    if (isNaN(numValue)) return;
    if (numValue < 0) {
      setFormData({ ...formData, rate: '0' });
    } else if (numValue > 100000) {
      setFormData({ ...formData, rate: '100000' });
    } else {
      setFormData({ ...formData, rate: raw });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-primary/20 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-black">Add Resource Assignment</DialogTitle>
          <DialogDescription className="text-gray">
            Add a new resource assignment to task: {selectedTaskId || 'Unknown'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div>
            <Label htmlFor="resource" className="text-black">Select Resource</Label>
            <Select value={formData.resource_id} onValueChange={onResourceChange} disabled={availableResources.length === 0}>
              <SelectTrigger className="bg-white border-primary/20 text-black">
                <SelectValue placeholder={availableResources.length > 0 ? "Select a resource" : "All resources are already assigned to this task"} />
              </SelectTrigger>
              <SelectContent>
                {availableResources.map((resource) => (
                  <SelectItem key={resource.id} value={resource.id}>
                    {resource.name} - {resource.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedResource && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="role" className="text-black">Role</Label>
                <Input
                  id="role"
                  type="text"
                  value={selectedResource.role || ''}
                  readOnly
                  className="bg-gray-50 border-gray text-black"
                />
              </div>
              <div>
                <Label htmlFor="role_type" className="text-black">Role Type</Label>
                <Input
                  id="role_type"
                  type="text"
                  value={selectedResource.role_type || ''}
                  readOnly
                  className="bg-gray-50 border-gray text-black"
                />
              </div>
              <div>
                <Label htmlFor="duration" className="text-black">Duration</Label>
                <Input
                  id="duration"
                  type="text"
                  value={selectedResource.capacity || ''}
                  readOnly
                  className="bg-gray-50 border-gray text-black"
                />
              </div>
              <div>
                <Label htmlFor="hours" className="text-black">Hours</Label>
                <Input
                  id="hours"
                  type="text"
                  value={selectedResource.availability || ''}
                  readOnly
                  className="bg-gray-50 border-gray text-black"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date" className="text-black">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="bg-white border-gray text-black"
              />
            </div>
            <div>
              <Label htmlFor="end_date" className="text-black">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="bg-white border-gray text-black"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="allocated_percentage" className="text-black">Allocation % (1-100)</Label>
              <Input
                id="allocated_percentage"
                type="number"
                min={1}
                max={100}
                value={formData.allocated_percentage}
                onChange={handleAllocationChange}
                onBlur={handleAllocationBlur}
                className="bg-white border-gray text-black"
              />
            </div>
            <div>
              <Label htmlFor="rate" className="text-black">Rate ($/hour) ($0-$100,000)</Label>
              <Input
                id="rate"
                type="text"
                value={formatRateWithCommas(String(formData.rate ?? ''))}
                onChange={handleRateChange}
                onBlur={handleRateBlur}
                className="bg-white border-gray text-black"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-primary/20">
            Cancel
          </Button>
          <Button 
            onClick={onSave} 
            className="bg-primary hover:bg-primary/90 text-black"
            disabled={availableResources.length === 0}
          >
            Add Resource
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddResourceAssignmentDialog;
